import React, { useState, useEffect } from 'react';
import { apiUrl } from '../config/api';
import './SideBySideCompare.css';

interface ScanResult {
  scanId: string;
  timestamp: string;
  profile: string;
  summary: {
    pass: boolean;
    totalFindings: number;
    failCount: number;
    warnCount: number;
    infoCount: number;
  };
  findings: Array<{
    code?: string;
    ruleId: string;
    severity: string;
    message: string;
    category: string;
    value?: number;
    limit?: number;
  }>;
  runtime?: {
    performance?: {
      initialLoadBytes: number;
      subloadBytes?: number;
      totalLoadBytes: number;
      loadTimeMs: number;
      domContentLoadedMs?: number;
      firstContentfulPaintMs?: number;
      initialRequests: number;
      totalRequests: number;
      politeLoadingPassed: boolean;
      mixedContentDetected: boolean;
      httpRequests: string[];
      animationDurationMs?: number;
      memoryUsageMb?: number;
      cpuUsageMs?: number;
      heavyAdRisk?: string;
    };
    screenshot?: {
      base64: string;
    };
    clickTest?: {
      clickTagFound: boolean;
      navigated: boolean;
      destinationUrl?: string;
    };
    consoleErrors?: {
      errorCount: number;
      errors: any[];
    };
    iframeCount?: number;
    dialogCount?: number;
  };
  metadata?: {
    originalFileName?: string;
    zipSize: number;
    fileCount: number;
    duplicateFilenames?: string[];
    extractedFiles: string[];
  };
}

interface CheckResult {
  status: 'pass' | 'fail' | 'warn' | 'info';
  value: string;
  rawValue?: number;
}

export const SideBySideCompare: React.FC = () => {
  const [scans, setScans] = useState<Array<{ scanId: string; fileName: string }>>([]);
  const [leftScanId, setLeftScanId] = useState<string>('');
  const [rightScanId, setRightScanId] = useState<string>('');
  const [leftScan, setLeftScan] = useState<ScanResult | null>(null);
  const [rightScan, setRightScan] = useState<ScanResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState<'overview' | 'findings' | 'screenshots'>('overview');

  useEffect(() => {
    loadAvailableScans();
  }, []);

  const loadAvailableScans = async () => {
    try {
      const response = await fetch(apiUrl('/scans?limit=50'), { credentials: 'include' });
      const data = await response.json();

      const scansWithNames = await Promise.all(
        (data.scans || []).map(async (scan: any) => {
          try {
            const detailResponse = await fetch(apiUrl(`/scans/${scan.scanId}`), { credentials: 'include' });
            const detailData = await detailResponse.json();
            return {
              scanId: scan.scanId,
              fileName: detailData.result?.metadata?.originalFileName || scan.scanId.substring(0, 8)
            };
          } catch {
            return { scanId: scan.scanId, fileName: scan.scanId.substring(0, 8) };
          }
        })
      );
      
      setScans(scansWithNames);
    } catch (error) {
      console.error('Failed to load scans:', error);
    }
  };

  const loadScan = async (scanId: string, side: 'left' | 'right') => {
    if (!scanId) return;

    setLoading(true);
    try {
      const response = await fetch(apiUrl(`/scans/${scanId}`), { credentials: 'include' });
      const data = await response.json();
      
      if (side === 'left') {
        setLeftScan(data.result);
      } else {
        setRightScan(data.result);
      }
    } catch (error) {
      console.error(`Failed to load ${side} scan:`, error);
    } finally {
      setLoading(false);
    }
  };

  const handleLeftChange = (scanId: string) => {
    setLeftScanId(scanId);
    loadScan(scanId, 'left');
  };

  const handleRightChange = (scanId: string) => {
    setRightScanId(scanId);
    loadScan(scanId, 'right');
  };

  const exportComparison = () => {
    if (!leftScan || !rightScan) return;

    // Sanitize value for CSV (remove Unicode, clean formatting)
    const sanitizeForCSV = (value: string) => {
      return value
        .replace(/✓/g, 'Pass')
        .replace(/✗/g, 'Fail')
        .replace(/âœ"/g, 'Pass')  // Corrupted checkmark
        .replace(/"/g, '""');  // Escape quotes
    };

    // Clean filenames for headers
    const leftName = (leftScan.metadata?.originalFileName || 'Left').replace(/\.(zip|html)$/i, '');
    const rightName = (rightScan.metadata?.originalFileName || 'Right').replace(/\.(zip|html)$/i, '');

    const rows = checks.map(check => {
      const leftResult = getCheckValue(leftScan, check.id);
      const rightResult = getCheckValue(rightScan, check.id);
      
      return {
        Check: check.name,
        Category: check.category,
        [leftName]: sanitizeForCSV(leftResult.value),
        [rightName]: sanitizeForCSV(rightResult.value)
      };
    });

    // Add summary at top
    const summary = {
      Check: 'SUMMARY',
      Category: 'Overall',
      [leftName]: `${leftScan.summary.pass ? 'PASS' : 'FAIL'} (${leftScan.summary.failCount} fail, ${leftScan.summary.warnCount} warn)`,
      [rightName]: `${rightScan.summary.pass ? 'PASS' : 'FAIL'} (${rightScan.summary.failCount} fail, ${rightScan.summary.warnCount} warn)`
    };

    const allRows = [summary, ...rows];

    // Convert to CSV
    const headers = Object.keys(allRows[0]);
    const csvContent = [
      headers.join(','),
      ...allRows.map(row => headers.map(h => `"${row[h as keyof typeof row]}"`).join(','))
    ].join('\n');

    // Download
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `comparison_${leftName}_vs_${rightName}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const formatBytes = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const formatMs = (ms: number) => {
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  };

  const getCheckValue = (scan: ScanResult | null, checkId: string): CheckResult => {
    if (!scan) return { status: 'pass', value: 'N/A' };

    const finding = scan.findings.find(f => f.ruleId === checkId || f.code === checkId);
    
    // If there's a finding, it's a failure/warning
    if (finding) {
      const displayValue = finding.value !== undefined 
        ? `${formatBytes(finding.value)} ${finding.limit ? `(limit: ${formatBytes(finding.limit)})` : ''}`
        : finding.message;
      
      return {
        status: finding.severity as any,
        value: displayValue,
        rawValue: finding.value
      };
    }

    // No finding means it passed - show the actual value
    switch (checkId) {
      case 'zip-size':
        return { status: 'pass', value: formatBytes(scan.metadata?.zipSize || 0), rawValue: scan.metadata?.zipSize };
      
      case 'file-count':
        return { status: 'pass', value: `${scan.metadata?.fileCount || 0} files`, rawValue: scan.metadata?.fileCount };
      
      case 'load-initial':
        return { 
          status: 'pass', 
          value: formatBytes(scan.runtime?.performance?.initialLoadBytes || 0),
          rawValue: scan.runtime?.performance?.initialLoadBytes
        };
      
      case 'load-subload':
        return { 
          status: 'pass', 
          value: formatBytes(scan.runtime?.performance?.subloadBytes || 0),
          rawValue: scan.runtime?.performance?.subloadBytes
        };
      
      case 'load-total':
        return { 
          status: 'pass', 
          value: formatBytes(scan.runtime?.performance?.totalLoadBytes || 0),
          rawValue: scan.runtime?.performance?.totalLoadBytes
        };
      
      case 'load-time':
        return { 
          status: 'pass', 
          value: formatMs(scan.runtime?.performance?.loadTimeMs || 0),
          rawValue: scan.runtime?.performance?.loadTimeMs
        };
      
      case 'request-count':
        return { 
          status: 'pass', 
          value: `${scan.runtime?.performance?.totalRequests || 0} requests`,
          rawValue: scan.runtime?.performance?.totalRequests
        };
      
      case 'animation-duration':
        const animDuration = scan.runtime?.performance?.animationDurationMs;
        return { 
          status: 'pass', 
          value: animDuration ? formatMs(animDuration) : 'N/A',
          rawValue: animDuration
        };
      
      case 'memory-usage':
        const memUsage = scan.runtime?.performance?.memoryUsageMb;
        return { 
          status: 'pass', 
          value: memUsage ? `${memUsage.toFixed(1)} MB` : 'N/A',
          rawValue: memUsage
        };
      
      case 'cpu-usage':
        const cpuUsage = scan.runtime?.performance?.cpuUsageMs;
        return { 
          status: 'pass', 
          value: cpuUsage ? formatMs(cpuUsage) : 'N/A',
          rawValue: cpuUsage
        };
      
      case 'console-errors':
        return { 
          status: 'pass', 
          value: `${scan.runtime?.consoleErrors?.errorCount || 0} errors`,
          rawValue: scan.runtime?.consoleErrors?.errorCount
        };
      
      case 'nested-iframes':
        return { 
          status: 'pass', 
          value: `${scan.runtime?.iframeCount || 0} iframes`,
          rawValue: scan.runtime?.iframeCount
        };
      
      case 'dialogs':
        return { 
          status: 'pass', 
          value: `${scan.runtime?.dialogCount || 0} dialogs`,
          rawValue: scan.runtime?.dialogCount
        };
      
      case 'ssl-required':
        return { status: 'pass', value: 'HTTPS Enforced' };
      
      case 'clicktag-exists':
        return { status: 'pass', value: 'ClickTag Found' };
      
      case 'clicktag-format':
        return { status: 'pass', value: scan.runtime?.clickTest?.destinationUrl || 'Valid Format' };
      
      case 'html-structure':
        return { status: 'pass', value: 'Valid HTML' };
      
      case 'polite-loading':
        return { status: 'pass', value: scan.runtime?.performance?.politeLoadingPassed ? 'Passed' : 'N/A' };
      
      case 'mixed-content':
        return { status: 'pass', value: scan.runtime?.performance?.mixedContentDetected ? 'Detected' : 'None' };
      
      case 'allowed-extensions':
        return { status: 'pass', value: 'All Valid' };
      
      case 'clicktag-navigation':
        return { status: 'pass', value: scan.runtime?.clickTest?.clickTagFound ? 'Working' : 'Not Tested' };
      
      default:
        return { status: 'pass', value: '✓ Pass' };
    }
  };

  const checks = [
    { id: 'zip-size', name: 'ZIP File Size', category: 'Static' },
    { id: 'file-count', name: 'File Count', category: 'Static' },
    { id: 'allowed-extensions', name: 'Allowed Extensions', category: 'Static' },
    { id: 'ssl-required', name: 'HTTPS Required', category: 'Security' },
    { id: 'clicktag-exists', name: 'ClickTag Exists', category: 'Static' },
    { id: 'clicktag-format', name: 'ClickTag Format', category: 'Static' },
    { id: 'html-structure', name: 'HTML Structure', category: 'Static' },
    { id: 'load-initial', name: 'Initial Load Size', category: 'Performance' },
    { id: 'load-subload', name: 'Subload Size', category: 'Performance' },
    { id: 'load-total', name: 'Total Load Size', category: 'Performance' },
    { id: 'load-time', name: 'Load Time', category: 'Performance' },
    { id: 'polite-loading', name: 'Polite Loading', category: 'Performance' },
    { id: 'mixed-content', name: 'Mixed Content', category: 'Security' },
    { id: 'request-count', name: 'Request Count', category: 'Performance' },
    { id: 'console-errors', name: 'Console Errors', category: 'Runtime' },
    { id: 'clicktag-navigation', name: 'ClickTag Navigation', category: 'Runtime' },
    { id: 'nested-iframes', name: 'Nested iFrames', category: 'Security' },
    { id: 'animation-duration', name: 'Animation Duration', category: 'Performance' },
    { id: 'memory-usage', name: 'Memory Usage', category: 'Performance' },
    { id: 'cpu-usage', name: 'CPU Usage', category: 'Performance' },
    { id: 'dialogs', name: 'Dialogs/Alerts', category: 'Security' },
  ];

  return (
    <div className="side-by-side-compare">
      <div className="compare-header">
        <h2>Side-by-Side Comparison</h2>
        <div className="view-toggle">
          {leftScan && rightScan && (
            <button
              onClick={exportComparison}
              style={{
                backgroundColor: '#10b981',
                color: '#fff',
                border: 'none',
                cursor: 'pointer',
                fontWeight: 500,
                fontSize: '14px',
                padding: '8px 16px',
                borderRadius: '6px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              📄 Export CSV
            </button>
          )}
          <button 
            className={view === 'overview' ? 'active' : ''}
            onClick={() => setView('overview')}
          >
            Overview
          </button>
          <button 
            className={view === 'findings' ? 'active' : ''}
            onClick={() => setView('findings')}
          >
            All Checks{leftScan && rightScan ? ` (${checks.length})` : ""}
          </button>
          <button 
            className={view === 'screenshots' ? 'active' : ''}
            onClick={() => setView('screenshots')}
          >
            Screenshots
          </button>
        </div>
      </div>

      <div className="scan-selectors">
        <div className="selector">
          <label>Left Scan:</label>
          <select value={leftScanId} onChange={(e) => handleLeftChange(e.target.value)}>
            <option value="">Select a scan...</option>
            {scans.map(scan => (
              <option key={scan.scanId} value={scan.scanId}>
                {scan.fileName}
              </option>
            ))}
          </select>
        </div>

        <div className="selector">
          <label>Right Scan:</label>
          <select value={rightScanId} onChange={(e) => handleRightChange(e.target.value)}>
            <option value="">Select a scan...</option>
            {scans.map(scan => (
              <option key={scan.scanId} value={scan.scanId}>
                {scan.fileName}
              </option>
            ))}
          </select>
        </div>
      </div>

      {loading && <div className="loading">Loading...</div>}

      {leftScan && rightScan && (
        <div className="comparison-view">
          {view === 'overview' && (
            <div className="overview-comparison">
              <div className="scan-column">
                <h3>{leftScan.metadata?.originalFileName || 'Left Scan'}</h3>
                <div className={`status-badge-compact ${leftScan.summary.pass ? 'pass' : 'fail'}`}>
                  {leftScan.summary.pass ? 'PASS' : 'FAIL'}
                </div>
                <div className="metrics">
                  <div className="metric">
                    <span className="label">Total Findings:</span>
                    <span className="value">{leftScan.summary.totalFindings}</span>
                  </div>
                  <div className="metric fail">
                    <span className="label">Failures:</span>
                    <span className="value">{leftScan.summary.failCount}</span>
                  </div>
                  <div className="metric warn">
                    <span className="label">Warnings:</span>
                    <span className="value">{leftScan.summary.warnCount}</span>
                  </div>
                  <div className="metric info">
                    <span className="label">Info:</span>
                    <span className="value">{leftScan.summary.infoCount}</span>
                  </div>
                  {leftScan.runtime?.performance && (
                    <>
                      <div className="metric">
                        <span className="label">Initial Load:</span>
                        <span className="value">{formatBytes(leftScan.runtime.performance.initialLoadBytes)}</span>
                      </div>
                      <div className="metric">
                        <span className="label">Total Load:</span>
                        <span className="value">{formatBytes(leftScan.runtime.performance.totalLoadBytes)}</span>
                      </div>
                      <div className="metric">
                        <span className="label">Load Time:</span>
                        <span className="value">{formatMs(leftScan.runtime.performance.loadTimeMs)}</span>
                      </div>
                    </>
                  )}
                </div>
              </div>

              <div className="divider"></div>

              <div className="scan-column">
                <h3>{rightScan.metadata?.originalFileName || 'Right Scan'}</h3>
                <div className={`status-badge-compact ${rightScan.summary.pass ? 'pass' : 'fail'}`}>
                  {rightScan.summary.pass ? 'PASS' : 'FAIL'}
                </div>
                <div className="metrics">
                  <div className="metric">
                    <span className="label">Total Findings:</span>
                    <span className="value">{rightScan.summary.totalFindings}</span>
                  </div>
                  <div className="metric fail">
                    <span className="label">Failures:</span>
                    <span className="value">{rightScan.summary.failCount}</span>
                  </div>
                  <div className="metric warn">
                    <span className="label">Warnings:</span>
                    <span className="value">{rightScan.summary.warnCount}</span>
                  </div>
                  <div className="metric info">
                    <span className="label">Info:</span>
                    <span className="value">{rightScan.summary.infoCount}</span>
                  </div>
                  {rightScan.runtime?.performance && (
                    <>
                      <div className="metric">
                        <span className="label">Initial Load:</span>
                        <span className="value">{formatBytes(rightScan.runtime.performance.initialLoadBytes)}</span>
                      </div>
                      <div className="metric">
                        <span className="label">Total Load:</span>
                        <span className="value">{formatBytes(rightScan.runtime.performance.totalLoadBytes)}</span>
                      </div>
                      <div className="metric">
                        <span className="label">Load Time:</span>
                        <span className="value">{formatMs(rightScan.runtime.performance.loadTimeMs)}</span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          {view === 'findings' && (
            <div className="findings-comparison-table">
              <table>
                <thead>
                  <tr>
                    <th style={{ width: '20%' }}>CHECK</th>
                    <th style={{ width: '40%' }}>{leftScan.metadata?.originalFileName?.toUpperCase() || 'LEFT SCAN'}</th>
                    <th style={{ width: '40%' }}>{rightScan.metadata?.originalFileName?.toUpperCase() || 'RIGHT SCAN'}</th>
                  </tr>
                </thead>
                <tbody>
                  {checks.map(check => {
                    const leftResult = getCheckValue(leftScan, check.id);
                    const rightResult = getCheckValue(rightScan, check.id);
                    
                    return (
                      <tr key={check.id}>
                        <td className="check-name">
                          <div className="check-title">{check.name}</div>
                          <div className="check-category">{check.category}</div>
                        </td>
                        <td className={`check-result ${leftResult.status}`}>
                          <span className="check-value">{leftResult.value}</span>
                        </td>
                        <td className={`check-result ${rightResult.status}`}>
                          <span className="check-value">{rightResult.value}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {view === 'screenshots' && (
            <div className="screenshots-comparison">
              <div className="scan-column">
                <h3>Screenshot</h3>
                {leftScan.runtime?.screenshot?.base64 ? (
                  <div className="screenshot-container">
                    <img 
                      src={`data:image/jpeg;base64,${leftScan.runtime.screenshot.base64}`}
                      alt="Left scan screenshot"
                      className="screenshot"
                    />
                  </div>
                ) : (
                  <div className="no-screenshot">No screenshot available</div>
                )}
              </div>

              <div className="divider"></div>

              <div className="scan-column">
                <h3>Screenshot</h3>
                {rightScan.runtime?.screenshot?.base64 ? (
                  <div className="screenshot-container">
                    <img 
                      src={`data:image/jpeg;base64,${rightScan.runtime.screenshot.base64}`}
                      alt="Right scan screenshot"
                      className="screenshot"
                    />
                  </div>
                ) : (
                  <div className="no-screenshot">No screenshot available</div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {(!leftScan || !rightScan) && !loading && (
        <div className="empty-state">
          <p>Select two scans to compare them side by side</p>
        </div>
      )}
    </div>
  );
};
